import type { UiText } from '@ory/kratos-client';

import {
  BIFROST_ERR_INVALID_CREDENTIALS,
  BIFROST_ERR_LOGIN_FLOW_EXPIRED,
  BIFROST_ERR_RECOVERY_FLOW_EXPIRED,
  BIFROST_ERR_REGISTRATION_FLOW_EXPIRED,
  BIFROST_ERR_SETTINGS_FLOW_EXPIRED,
  BIFROST_ERR_VERIFICATION_FLOW_EXPIRED,
} from './bifrost-message-ids';

/**
 * Kratos's own expired-flow text ("The login flow expired 1.00 minutes ago, please try again.")
 * tells the user the flow expired but not what to do about it -- the fix is to reload the page so a
 * fresh flow is issued. Same copy for every flow so the instruction is consistent wherever a stale
 * form is submitted.
 */
/**
 * Shown whenever a Kratos self-service flow (login, registration, verification, recovery,
 * settings) has expired. A flow is single-use and short-lived, so the fix is to reload the page
 * for a fresh one -- Kratos's default text ("expired N minutes ago, please try again") does not
 * say that. Used in two places that must agree:
 *   - the client-side expiry guard in each `use-submit-*-mutation` throws it directly, which is the
 *     string users actually see in the common case;
 *   - `MESSAGE_OVERRIDES` maps the server-returned expired-flow messages to it, so a flow Kratos
 *     rejects as expired reads identically.
 */
export const FLOW_EXPIRED_MESSAGE =
  'This form has expired. Please reload the page to start again.';

// Re-export the SDK type so callers don't need to import from both packages.
export type { UiText };

/** A static string override. */
type StaticOverride = string;

/** A dynamic override that receives the message context and returns a string. */
type DynamicOverride = (context: Record<string, string | number>) => string;

export type MessageOverride = StaticOverride | DynamicOverride;

/**
 * Overrides for Bifrost (Ory Kratos) UI messages.
 *
 * Republic only supports email + password authentication, so messages that
 * reference usernames or phone numbers are replaced with email-only phrasing.
 *
 * Keys are Bifrost message IDs. Values are either a replacement string or a
 * function that receives the message `context` and returns a string.
 */
export const MESSAGE_OVERRIDES: Partial<Record<number, MessageOverride>> = {
  [BIFROST_ERR_INVALID_CREDENTIALS]:
    'The provided credentials are invalid, check for spelling mistakes in your email address or password.',
  [BIFROST_ERR_LOGIN_FLOW_EXPIRED]: FLOW_EXPIRED_MESSAGE,
  [BIFROST_ERR_REGISTRATION_FLOW_EXPIRED]: FLOW_EXPIRED_MESSAGE,
  [BIFROST_ERR_VERIFICATION_FLOW_EXPIRED]: FLOW_EXPIRED_MESSAGE,
  [BIFROST_ERR_RECOVERY_FLOW_EXPIRED]: FLOW_EXPIRED_MESSAGE,
  [BIFROST_ERR_SETTINGS_FLOW_EXPIRED]: FLOW_EXPIRED_MESSAGE,
};

/**
 * Returns the display text for a Bifrost UI message.
 *
 * If a Republic-specific override exists for the given message ID, that
 * override is returned. Otherwise the original `text` from Bifrost is used.
 *
 * @example
 * ```ts
 * const message = getBifrostMessage({ id: 4000006, text: '...original...', type: 'error' });
 * // → 'The provided credentials are invalid, check for spelling mistakes in your email address or password.'
 * ```
 */
export function getBifrostMessage(uiText: UiText): string {
  const override = MESSAGE_OVERRIDES[uiText.id];

  if (override === undefined) {
    return uiText.text;
  }

  if (typeof override === 'function') {
    return override((uiText.context as Record<string, string | number>) ?? {});
  }

  return override;
}
